import { formatMeasurement, measurementUnit } from './measurement'
import type { MeasurementSystem } from './measurementSystem'
import { getBaseItemId } from './optimizationUtils'
import type { OptimizationRun, UnloadedItem, Item3D } from '../types/optimization'

export interface EnrichedUnloadedItem extends UnloadedItem {
  description?: string
  length?: number
  width?: number
  height?: number
  weight?: number
  /** Number of unloaded packages represented by this row, not the article total. */
  quantity?: number
  color?: string
}

const REASON_LABELS: Record<string, string> = {
  UNKNOWN_REASON: 'Motif non fourni par le moteur',
  PALLET_FOOTPRINT_EXCEEDED: 'Colis hors gabarit de la palette',
  PALLET_HEIGHT_EXCEEDED: 'Hauteur de palette chargée dépassée',
  PALLET_INVALID_DIMENSIONS: 'Dimensions de palettisation invalides',
  PALLET_INVALID_WEIGHT: 'Poids de palettisation invalide',
  PALLET_WEIGHT_EXCEEDED: 'Poids maximum de palette dépassé',
  PALLET_INVALID_STACKING_CONSTRAINTS: 'Contraintes d’empilement invalides',
  PALLET_STACKING_LIMIT_EXCEEDED: 'Limite d’empilement palette dépassée',
  NO_FEASIBLE_PLACEMENT: 'Aucun placement possible',
  CANDIDATE_BUDGET_EXHAUSTED: 'Budget candidats épuisé',
  NO_CANDIDATE_GENERATED: 'Aucun candidat généré',
  ITEM_EXCEEDS_LOADSPACE: 'Dimensions incompatibles',
  NO_FREE_SPACE_FITS_ITEM: 'Aucun espace libre adapté',
  ALL_CANDIDATES_REJECTED_BY_CONSTRAINT: 'Aucun emplacement ne respecte les contraintes',
  STACKABILITY_VIOLATION: 'Blocage par règles d\'empilage',
  UNSUPPORTED_STACKING: 'Support insuffisant',
  COLLISION: 'Collision avec un article chargé',
  OUT_OF_BOUNDS: 'Dépasse les limites du conteneur',
  ROTATION_RESTRICTION: 'Rotation non autorisée',
  ADR_INCOMPATIBLE: 'Incompatibilité ADR',
  ADR_LOADSPACE_INCOMPATIBLE: 'Conteneur incompatible ADR',
  TEMPERATURE_INCOMPATIBLE: 'Incompatibilité de température',
  too_large: 'Dimensions trop grandes',
  weight_limit: 'Limite de poids dépassée',
  no_space: 'Espace insuffisant',
  fragility_violation: 'Contrainte de fragilité non respectée',
  orientation_constraint: 'Orientation incompatible',
  adr_incompatibility: 'Incompatibilité ADR',
  total_weight: 'Poids total autorisé dépassé',
  axle_overload: 'Charge maximale d’essieu dépassée',
  axle_group_overload: 'Charge maximale du groupe d’essieux dépassée',
  king_pin_overload: 'Charge maximale de sellette dépassée',
  floor_pressure_unverified: 'Pression au plancher non vérifiée',
  floor_pressure_exceeded: 'Pression au plancher dépassée',
  unsupported_load: 'Support porteur absent',
  load_path_moment_unverified: 'Chemin de charge à vérifier',
}

export function formatReason(reason?: string, dominantReason?: string): string {
  if (dominantReason && REASON_LABELS[dominantReason]) return REASON_LABELS[dominantReason]
  if (!reason) return 'Non spécifié'
  return REASON_LABELS[reason] ?? 'Autre contrainte de chargement'
}

export function reasonDetail(item: UnloadedItem): string | undefined {
  // Pour les incompatibilités de température, afficher les détails enrichis si disponibles
  if (item.dominant_rejection_reason === 'TEMPERATURE_INCOMPATIBLE') {
    const metadata = item.rejection_summary?.temperature_metadata;
    const sourceRange = metadata?.sourceRange;
    const targetRange = metadata?.targetRange;

    // Si on a les métadonnées complètes, afficher le message formaté
    if (sourceRange && targetRange && sourceRange.controlled && targetRange.controlled) {
      const sourceTemp = `${sourceRange.min ?? '?'}°${sourceRange.unit || 'C'} à ${sourceRange.max ?? '?'}°${sourceRange.unit || 'C'}`;
      const targetTemp = `${targetRange.min ?? '?'}°${targetRange.unit || 'C'} à ${targetRange.max ?? '?'}°${targetRange.unit || 'C'}`;
      return `Produit: ${sourceTemp} | Espace: ${targetTemp}`;
    }
  }

  if (item.dominant_rejection_reason === 'load_path_moment_unverified') {
    return 'Le moteur a détecté un empilement avec contact porteur, mais le modèle de pression uniforme ne sait pas prouver l’équilibre des moments. Cette information doit apparaître comme un avertissement de contrôle avancé, pas comme une cause bloquante si la pression, le support et la charge utile sont conformes.'
  }

  // Sinon, utiliser le message standard du backend
  return item.reason_detail || item.rejection_summary?.message;
}

export function diagnosticDetail(item: UnloadedItem, system?: MeasurementSystem): string | undefined {
  const dimensions = (d: { length?: number; width?: number; height?: number }, cargo = false) => system
    ? [d.length, d.width, d.height].map(value => formatMeasurement(value, 'mm', measurementUnit(system, cargo ? 'cargoLength' : 'vehicleLength'))).join(' × ')
    : `${d.length}×${d.width}×${d.height} mm`
  const diagnostics = item.rejection_summary?.diagnostics
  if (!diagnostics) return undefined

  const itemDims = diagnostics.item_dimensions_mm
  const loadSpaceDims = diagnostics.load_space_dimensions_mm
  const largestFreeSpace = diagnostics.largest_free_space
  const parts = []

  if (itemDims) {
    parts.push(`Article: ${dimensions(itemDims, true)}`)
  }
  if (loadSpaceDims) {
    parts.push(`Conteneur: ${dimensions(loadSpaceDims)}`)
  }
  if (largestFreeSpace) {
    parts.push(`Plus grand espace libre: ${dimensions(largestFreeSpace)}`)
  }
  if (diagnostics.free_spaces_total != null || diagnostics.free_spaces_fitting_item != null) {
    parts.push(`Espaces compatibles: ${diagnostics.free_spaces_fitting_item ?? '?'} / ${diagnostics.free_spaces_total ?? '?'}`)
  }
  if (diagnostics.heuristic_strategy) {
    parts.push(`Stratégie: ${diagnostics.heuristic_strategy}`)
  }
  if (diagnostics.rotations_tested != null || diagnostics.rotations_fitting_load_space != null) {
    parts.push(`Rotations compatibles: ${diagnostics.rotations_fitting_load_space ?? '?'} / ${diagnostics.rotations_tested ?? '?'}`)
  }

  return parts.length ? parts.join(' | ') : undefined
}

function findInputItem(items: Item3D[], unloaded: UnloadedItem): Item3D | undefined {
  const sourceId = (unloaded as any).source_id ?? (unloaded as any).metadata?.source_id
  if (sourceId) {
    const source = items.find(item => item.id === sourceId)
    if (source) return source
  }
  let matched = items.find((item) => item.id === unloaded.id || item.id === unloaded.reference)
  if (!matched) {
    const checkId = (id?: string) => {
      if (!id || !id.includes('_')) return undefined
      const parts = id.split('_')
      if (!isNaN(Number(parts[parts.length - 1]))) {
        return items.find((item) => item.id === parts.slice(0, -1).join('_'))
      }
      return undefined
    }
    matched = checkId(unloaded.id) || checkId(unloaded.reference)
  }
  return matched
}

/**
 * Extrait les articles non placés depuis le résultat backend,
 * avec repli sur le calcul différentiel input/placements si absent.
 */
export function extractUnloadedItems(run: OptimizationRun): EnrichedUnloadedItem[] {
  const raw = run.result?.unloadedItems ?? run.result?.unloaded_items
  const palletGroups = run.result?.palletGroups ?? run.result?.pallet_groups ?? []
  const palletGroupMap = new Map(palletGroups.map(pg => [pg.pallet_id, pg]))

  if (Array.isArray(raw)) {
    const expandedUnloaded: UnloadedItem[] = []

    for (const u of raw) {
      const pg = palletGroupMap.get(u.id)
      if (pg) {
        if (pg.children_positions?.length) {
          const counts = new Map<string, number>()
          for (const child of pg.children_positions) {
            counts.set(child.item_id, (counts.get(child.item_id) || 0) + 1)
          }
          for (const [itemId, qty] of counts.entries()) {
            expandedUnloaded.push({
              ...pg.children_positions.find(child => child.item_id === itemId),
              id: itemId,
              reason: u.reason || 'UNKNOWN_REASON',
              reason_detail: u.reason_detail,
              rejection_summary: u.rejection_summary,
              dominant_rejection_reason: u.dominant_rejection_reason,
              quantity: qty
            } as any)
          }
        } else if (pg.children_item_ids?.length) {
          const counts = new Map<string, number>()
          for (const cid of pg.children_item_ids || []) {
            const baseId = cid
            counts.set(baseId, (counts.get(baseId) || 0) + 1)
          }
          for (const [baseId, qty] of counts.entries()) {
            expandedUnloaded.push({
              id: baseId,
              reason: u.reason || 'UNKNOWN_REASON',
              reason_detail: u.reason_detail,
              rejection_summary: u.rejection_summary,
              dominant_rejection_reason: u.dominant_rejection_reason,
              quantity: qty
            } as any)
          }
        } else {
          expandedUnloaded.push(u)
        }
      } else {
        expandedUnloaded.push(u)
      }
    }

    const aggregated = new Map<string, EnrichedUnloadedItem>()
    for (const u of expandedUnloaded) {
      const inputItem = findInputItem(run.input.items, u)
      const key = `${(u as any).source_id ?? (u as any).metadata?.source_id ?? ''}:${u.id}`
      const existing = aggregated.get(key)
      // Backend rejections identify individual expanded units (e.g. 21_3).
      // Only an explicit quantity denotes a grouped row; catalog quantities
      // describe the entire article and must not be copied onto each unit.
      const qty = (u as EnrichedUnloadedItem).quantity ?? 1

      if (existing) {
        if (existing.quantity !== undefined) {
          existing.quantity += qty
        }
      } else {
        aggregated.set(key, {
          ...u,
          description: inputItem?.description ?? (u as EnrichedUnloadedItem).description,
          length: inputItem?.length ?? (u as EnrichedUnloadedItem).length,
          width: inputItem?.width ?? (u as EnrichedUnloadedItem).width,
          height: inputItem?.height ?? (u as EnrichedUnloadedItem).height,
          weight: inputItem?.weight ?? (u as EnrichedUnloadedItem).weight,
          quantity: qty,
          color: inputItem?.color ?? (u as EnrichedUnloadedItem).color,
        })
      }
    }

    return Array.from(aggregated.values())
  }

  const inputIds = new Set(run.input.items.map(item => item.id))
  const loadedCounts = new Map<string, number>()
  for (const placement of run.result?.placements ?? []) {
    const id = placement.item_id ?? placement.itemId ?? ''
    const group = palletGroupMap.get(id)
    const children = group?.children_positions?.length
      ? group.children_positions.map(child => ({ id: child.item_id, sourceId: child.source_id }))
      : (group?.children_item_ids ?? [id]).map(id => ({ id, sourceId: (placement as any).metadata?.source_id }))
    for (const child of children) {
      const baseId = getBaseItemId(child.id, inputIds, child.sourceId)
      loadedCounts.set(baseId, (loadedCounts.get(baseId) ?? 0) + 1)
    }
  }
  return run.input.items.flatMap(item => {
    const quantity = Math.max(0, (item.quantity ?? 1) - (loadedCounts.get(item.id) ?? 0))
    return quantity ? [{ ...item, reference: item.id, reason: 'UNKNOWN_REASON', quantity }] : []
  })
}
